// ---------------------------------------------------------------------------
// Event Hierarchy Service — builds a 2-level tree from app data:
// Application > Block > Event
// ---------------------------------------------------------------------------

import type { App } from '@cogo/types-v2'
import type { BlockDef } from '@cogo/types-v2'
import type { EventBinding } from '@cogo/types-v2'
import { detectBlockSpec, getRequiredEvents } from '@/features/flow-editor/services/bundle-spec-service'
import { buildActionExecutionModel } from '@/features/flow-editor/services/actionflow-execution-model'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface EventHierarchyNode {
  id: string
  type: 'app' | 'block' | 'event'
  label: string
  children: EventHierarchyNode[]
  // For event nodes:
  eventType?: string       // 'tap', 'change', etc.
  flowName?: string        // connected ActionFlow name
  actionName?: string      // directly bound atomic action
  bindingKind?: 'flow' | 'action'
  binding?: EventBinding
  triggerType?: 'event' | 'init'
  params?: Record<string, unknown>
  status?: 'spec' | 'user' | 'orphan'
  blockName?: string
  elementId?: string
  narrative?: import('@cogo/types').Narrative | null
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Convert ROLE elementId like `{ROLE:add-to-cart}` to human-readable label */
export function roleToLabel(elementId: string): string {
  const roleMatch = elementId.match(/^\{ROLE:(.+)\}$/)
  if (!roleMatch) return elementId
  return roleMatch[1]
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

// ---------------------------------------------------------------------------
// Main builder
// ---------------------------------------------------------------------------

export function buildEventHierarchy(
  app: App,
  blocks: Record<string, BlockDef>,
): EventHierarchyNode {
  const root: EventHierarchyNode = {
    id: `app:${app.appId}`,
    type: 'app',
    label: app.name || app.appId,
    children: [],
  }

  // Collect all unique block names across all pages.
  // Defensive: empty/new projects may have `app.pages` undefined or null;
  // returning the empty hierarchy keeps the IDE shell renderable instead of
  // crashing with "TypeError: app.pages is not iterable".
  const allBlockNames = new Set<string>()
  for (const page of app.pages ?? []) {
    for (const blockName of page.blocks ?? []) {
      allBlockNames.add(blockName)
    }
  }

  for (const blockName of allBlockNames) {
    const block = blocks[blockName]
    if (!block) continue

    const blockNode: EventHierarchyNode = {
      id: `block:${blockName}`,
      type: 'block',
      label: block.meta?.name ?? blockName,
      children: [],
      blockName,
    }

    // Spec detection for status tagging
    const spec = detectBlockSpec(blockName)
    const specEvents = spec ? getRequiredEvents(spec) : []
    const specFlowSet = new Set(specEvents.map((se) => se.flow.toLowerCase()))

    // Block flow names for orphan detection
    const rawFlow = (block.flow ?? {}) as Record<string, unknown>
    const flowObj = (rawFlow.flows ?? rawFlow) as Record<string, unknown>
    const blockFlowNames = new Set(Object.keys(flowObj).map((k) => k.toLowerCase()))

    // ---- Init flows (block-level events from flow.json) ----
    for (const [flowName, flowDef] of Object.entries(flowObj)) {
      const fd = flowDef as { trigger?: string; narrative?: EventHierarchyNode['narrative'] } | undefined
      if (fd?.trigger === 'init') {
        const eventNode: EventHierarchyNode = {
          id: `event:${blockName}:init:${flowName}`,
          type: 'event',
          label: flowName,
          children: [],
          eventType: 'init',
          flowName,
          bindingKind: 'flow',
          binding: { flow: flowName } as EventBinding,
          triggerType: 'init',
          status: specFlowSet.has(flowName.toLowerCase()) ? 'spec' : 'user',
          blockName,
          narrative: fd.narrative ?? null,
        }
        blockNode.children.push(eventNode)
      }
    }

    // ---- Event bindings (events.json) ----
    const rawEvents = block.events as Record<string, unknown> | undefined
    if (rawEvents && Object.keys(rawEvents).length > 0) {
      // Handle both { bindings: {...} } and flat { "elementId": { "tap": {...} } } formats
      const eventEntries = (rawEvents.bindings ?? rawEvents) as Record<string, unknown>

      for (const [key, value] of Object.entries(eventEntries)) {
        if (key === 'bindings') continue // skip if already unwrapped

        const val = value as Record<string, unknown>

        // Check if this is v2 EventMap format: value has nested objects with flow/action keys
        const isNestedFormat = val && typeof val === 'object' && !val.flow && !val.action &&
          Object.values(val).some((v) => v && typeof v === 'object' && ('flow' in (v as object) || 'action' in (v as object)))

        if (isNestedFormat) {
          // V2 format: key = elementId, value = Record<eventName, EventBinding>
          const elementId = key
          for (const [eventName, bindingRaw] of Object.entries(val)) {
            const binding = bindingRaw as EventBinding
            addEventToBlock(
              blockNode, elementId, eventName,
              binding, blockName, specFlowSet, blockFlowNames, flowObj,
            )
          }
        } else if (val && typeof val === 'object' && ('flow' in val || 'action' in val)) {
          // Legacy format: key = "elementId:eventType"
          // Use lastIndexOf for ROLE keys like "{ROLE:add-to-cart}:tap"
          const lastColon = key.lastIndexOf(':')
          const elementId = lastColon > 0 ? key.slice(0, lastColon) : key
          const eventType = lastColon > 0 ? key.slice(lastColon + 1) : 'tap'
          const binding = val as EventBinding
          addEventToBlock(
            blockNode, elementId, eventType,
            binding, blockName, specFlowSet, blockFlowNames, flowObj,
          )
        }
      }
    }

    // Only add block if it has events
    if (blockNode.children.length > 0) {
      root.children.push(blockNode)
    }
  }

  return root
}

// ---------------------------------------------------------------------------
// Internal helper: add event directly under block node
// ---------------------------------------------------------------------------

function addEventToBlock(
  blockNode: EventHierarchyNode,
  elementId: string,
  eventType: string,
  binding: EventBinding,
  blockName: string,
  specFlowSet: Set<string>,
  blockFlowNames: Set<string>,
  flowObj?: Record<string, unknown>,
) {
  const flowName = binding.flow ?? ''
  const actionName = binding.action ?? ''
  const targetName = flowName || actionName
  const bindingKind = actionName ? 'action' : 'flow'
  let status: 'spec' | 'user' | 'orphan' = 'user'
  if (specFlowSet.has(flowName.toLowerCase())) {
    status = 'spec'
  } else if (flowName && !blockFlowNames.has(flowName.toLowerCase())) {
    status = 'orphan'
  } else if (actionName && !buildActionExecutionModel(actionName).directBindingAllowed) {
    status = 'orphan'
  }

  // Look up narrative from block flow definition
  const flowDef = flowObj?.[flowName] as { narrative?: EventHierarchyNode['narrative'] } | undefined
  const narrative = flowDef?.narrative ?? null

  const eventNode: EventHierarchyNode = {
    id: `event:${blockName}:${elementId}:${eventType}`,
    type: 'event',
    label: `${eventType} -> ${targetName || '(none)'}`,
    children: [],
    eventType,
    flowName,
    actionName,
    bindingKind,
    binding,
    triggerType: 'event',
    params: (binding.params ?? {}) as Record<string, unknown>,
    status,
    blockName,
    elementId,
    narrative,
  }
  blockNode.children.push(eventNode)
}
